import { initializeApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
import { getAnalytics } from "firebase/analytics";
import { initializeFirestore, persistentLocalCache, connectFirestoreEmulator, setLogLevel } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getAuth, connectAuthEmulator } from "firebase/auth";
import logger from './utils/logger';

// Suppress Firestore internal warnings
setLogLevel('error');

// Validate environment variables
const validateConfig = (config) => {
    const required = ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'messagingSenderId', 'appId'];
    const missing = required.filter(key => {
        const value = config[key];
        return !value || typeof value !== 'string' || value.includes('your_');
    });
    
    if (missing.length > 0) {
        logger.error('Missing Firebase configuration:', missing);
        throw new Error(`Missing Firebase configuration: ${missing.join(', ')}`);
    }
};

// Firebase configuration from environment variables
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Validate configuration before initializing
validateConfig(firebaseConfig);

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// App Check (bot / abuse protection). Stays completely inert until a reCAPTCHA v3
// site key is provided via REACT_APP_APPCHECK_SITE_KEY, so existing clients and
// the server automation are never affected. Once the key is set here AND
// enforcement is turned on in the Firebase console, only the real app can reach
// Firestore/Auth from a browser (the server uses the Admin SDK, which is exempt).
try {
    const appCheckSiteKey = process.env.REACT_APP_APPCHECK_SITE_KEY;
    if (appCheckSiteKey) {
        initializeAppCheck(app, {
            provider: new ReCaptchaV3Provider(appCheckSiteKey),
            isTokenAutoRefreshEnabled: true
        });
        logger.info('App Check initialized');
    }
} catch (error) {
    logger.warn('App Check initialization skipped:', error);
}

// Initialize Analytics only in production
let analytics = null;
if (process.env.NODE_ENV === 'production' && firebaseConfig.measurementId) {
    try {
        analytics = getAnalytics(app);
    } catch (error) {
        logger.warn('Analytics initialization failed:', error);
    }
}

// Initialize Firestore with enhanced security settings
const db = initializeFirestore(app, {
    localCache: persistentLocalCache(),
    experimentalForceLongPolling: true,
    ignoreUndefinedProperties: true
});

// Initialize Auth with enhanced security
const auth = getAuth(app);

// Initialize Storage
const storage = getStorage(app);

// Connect to emulators in development only (never in production)
if (process.env.REACT_APP_USE_EMULATOR === 'true' && process.env.NODE_ENV === 'development') {
    try {
        connectFirestoreEmulator(db, 'localhost', 8080);
        logger.info('Connected to Firestore emulator');
    } catch (error) {
        logger.warn('Failed to connect to Firestore emulator:', error);
    }
    
    try {
        // CWE-319: HTTP allowed only for local development emulator
        connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
        logger.info('Connected to Auth emulator');
    } catch (error) {
        logger.warn('Failed to connect to Auth emulator:', error);
    }
    
    try {
        connectStorageEmulator(storage, 'localhost', 9199);
        logger.info('Connected to Storage emulator');
    } catch (error) {
        logger.warn('Failed to connect to Storage emulator:', error);
    }
}

// Export cleanup function for auth listener
let authUnsubscribe = null;

// Security monitoring (with cleanup support)
const initAuthMonitoring = () => {
    if (authUnsubscribe) return; // Already initialized
    
    authUnsubscribe = auth.onAuthStateChanged((user) => {
        if (user) {
            logger.info('User authenticated:', { uid: user.uid, email: user.email?.substring(0, 3) + '***' });
        } else {
            logger.info('User signed out');
        }
    });
};

// Cleanup function
const cleanupAuthMonitoring = () => {
    if (authUnsubscribe) {
        authUnsubscribe();
        authUnsubscribe = null;
    }
};

// Initialize monitoring
initAuthMonitoring();

export { app, db, auth, storage, analytics, cleanupAuthMonitoring };
